/**
 * Collaborative Puzzle Solving View
 * Allows multiple users to solve puzzles together with real-time synchronization
 * Teacher can see all students' boards in real-time
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Chess } from 'chess.js';
import { Chessboard } from 'react-chessboard';
import { toast } from 'react-toastify';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ChessPuzzle, getPuzzlesForSubscription, markPuzzleAsSolved } from '../../../../../services/chessPuzzleService';
import {
    CollaborativePuzzleSession,
    ParticipantBoardState,
    subscribeToSession,
    subscribeToParticipant,
    subscribeToParticipantsList,
    updateParticipantBoardState,
    leaveCollaborativeSession,
    endCollaborativeSession,
    deleteCollaborativeSession,
    joinCollaborativeSession,
    switchSessionPuzzle
} from '../../../../../services/collaborativePuzzleService';
import { chessSounds } from '../../../../../utils/chessSounds';
import { useAppSelector } from '../../../../../state/state';
import {
    trackChequeredCollabSessionJoined,
    trackChequeredCollabPuzzleSwitched,
    trackChequeredCollabShareLinkCopied,
    trackChequeredCollabSessionEnded,
    trackChequeredCollabPuzzleSolved,
    trackChequeredPuzzleMarkedSolved
} from '../../../../../utils/analytics';
import { selectEffectiveUID } from '../../../../../state/UserStatesV2/userSliceV2';

interface CollaborativePuzzleViewProps {
    sessionId: string;
    subscriptionId: string;
    puzzle: ChessPuzzle;
    userId: string;
    userName: string;
    userRole: 'teacher' | 'student';
    onClose: () => void;
}

const CollaborativePuzzleView: React.FC<CollaborativePuzzleViewProps> = ({
    sessionId,
    subscriptionId,
    puzzle,
    userId,
    userName,
    userRole,
    onClose
}) => {
    const [game, setGame] = useState(new Chess(puzzle.fen));
    const [session, setSession] = useState<CollaborativePuzzleSession | null>(null);
    const [participants, setParticipants] = useState<{ [userId: string]: ParticipantBoardState }>({});
    const [boardOrientation, setBoardOrientation] = useState<'white' | 'black'>('white');
    const [puzzleCompleted, setPuzzleCompleted] = useState(false);
    const [boardShake, setBoardShake] = useState(false);
    const [soundEnabled, setSoundEnabled] = useState(() => {
        const saved = localStorage.getItem('chessSoundEnabled');
        return saved !== null ? saved === 'true' : true;
    });
    const [showDeleteWarning, setShowDeleteWarning] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const unsubscribeSessionRef = useRef<(() => void) | null>(null);
    const unsubscribeParticipantsRef = useRef<Array<() => void>>([]);
    const unsubscribeParticipantsListRef = useRef<(() => void) | null>(null);
    const subscribedParticipantIdsRef = useRef<Set<string>>(new Set());
    const hasJoinedRef = useRef(false);

    const isTeacher = userRole === 'teacher';
    const serviceProviderId = useAppSelector(selectEffectiveUID);

    // Analytics: track session joined (once on mount)
    useEffect(() => {
        if (!serviceProviderId || !userId || !sessionId) return;
        trackChequeredCollabSessionJoined({
            service_provider_id: serviceProviderId,
            user_id: userId,
            subscription_id: subscriptionId,
            user_name: userName,
            user_role: userRole,
            session_id: sessionId,
            role: userRole
        });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps -- intentional: fire once on mount

    // Class puzzle list for this subscription (teacher only)
    const [availablePuzzles, setAvailablePuzzles] = useState<(ChessPuzzle & { isSolved?: boolean })[] | null>(null);
    const [puzzleListLoading, setPuzzleListLoading] = useState(false);
    const [puzzleFilter, setPuzzleFilter] = useState<'all' | 'unsolved' | 'solved'>('all');

    // Active puzzle always comes from the session if available, otherwise from the initial prop
    const activePuzzle = session?.puzzle || puzzle;
    
    console.log('🎯 CollaborativePuzzleView rendered:', {
        userId,
        userName,
        userRole,
        isTeacher,
        sessionId,
        subscriptionId
    });

    // Load all puzzles for this subscription for teacher, so they can switch between them
    useEffect(() => {
        if (!isTeacher || !subscriptionId || !serviceProviderId) {
            return;
        }
        let cancelled = false;
        const loadPuzzles = async () => {
            try {
                setPuzzleListLoading(true);
                const puzzles = await getPuzzlesForSubscription(serviceProviderId, subscriptionId, { limit: 200 });
                if (!cancelled) {
                    setAvailablePuzzles(puzzles as (ChessPuzzle & { isSolved?: boolean })[]);
                }
            } catch (error) {
                console.error('Error loading subscription puzzles for collaborative view:', error);
                if (!cancelled) {
                    toast.error('Failed to load class puzzles for collaborative session');
                }
            } finally {
                if (!cancelled) {
                    setPuzzleListLoading(false);
                }
            }
        };
        loadPuzzles();
        return () => {
            cancelled = true;
        };
    }, [isTeacher, subscriptionId, serviceProviderId]);

    const handleSwitchPuzzle = useCallback(
        async (targetPuzzleId: string) => {
            if (!isTeacher || !subscriptionId) return;
            if (!availablePuzzles || availablePuzzles.length === 0) return;
            const target = availablePuzzles.find((p) => p.id === targetPuzzleId);
            if (!target) return;
            if (target.id === activePuzzle.id) return;
            try {
                toast.info('Switching collaborative puzzle...');
                // Optimistic update so list selection and local session state update immediately for teacher
                setSession((prev) =>
                    prev ? { ...prev, puzzleId: target.id, puzzle: target } : null
                );
                await switchSessionPuzzle(sessionId, subscriptionId, target);
                toast.success('Puzzle switched for everyone');
                trackChequeredCollabPuzzleSwitched({
                    service_provider_id: serviceProviderId,
                    user_id: userId,
                    subscription_id: subscriptionId,
                    user_name: userName,
                    user_role: userRole,
                    session_id: sessionId,
                    puzzle_id: target.id,
                    puzzle_description: target.description || undefined
                });
            } catch (error) {
                console.error('Error switching collaborative puzzle:', error);
                toast.error('Failed to switch puzzle');
                // Revert optimistic update on error
                setSession((prev) => (prev ? { ...prev, puzzleId: activePuzzle.id, puzzle: activePuzzle } : null));
            }
        },
        [isTeacher, subscriptionId, availablePuzzles, activePuzzle, sessionId]
    );

    const handleMarkAsSolved = useCallback(
        async (puzzleId: string) => {
            if (!isTeacher || !serviceProviderId || !subscriptionId) return;
            try {
                const ok = await markPuzzleAsSolved(serviceProviderId, subscriptionId, puzzleId, userId, userName);
                if (ok) {
                    trackChequeredPuzzleMarkedSolved({
                        service_provider_id: serviceProviderId,
                        user_id: userId,
                        subscription_id: subscriptionId,
                        user_name: userName,
                        user_role: userRole,
                        puzzle_id: puzzleId,
                        context: 'collaborative'
                    });
                    setAvailablePuzzles((prev) =>
                        prev ? prev.map((p) => (p.id === puzzleId ? { ...p, isSolved: true } : p)) : null
                    );
                    toast.success('Puzzle marked as solved');
                }
            } catch (error) {
                console.error('Error marking puzzle as solved:', error);
                toast.error('Failed to mark as solved');
            }
        },
        [isTeacher, serviceProviderId, subscriptionId, userId, userName]
    );

    const filteredPuzzles = availablePuzzles
        ? puzzleFilter === 'solved'
            ? availablePuzzles.filter((p) => p.isSolved)
            : puzzleFilter === 'unsolved'
            ? availablePuzzles.filter((p) => !p.isSolved)
            : availablePuzzles
        : [];

    // Reset local game state whenever the active puzzle changes
    useEffect(() => {
        try {
            const newGame = new Chess(activePuzzle.fen);
            setGame(newGame);
            setPuzzleProgress([]);
            setPuzzleCompleted(false);
            setIsCorrectMove(false);

            const turnColor = newGame.turn(); // 'w' for white, 'b' for black
            const orientation = turnColor === 'w' ? 'white' : 'black';
            setBoardOrientation(orientation);
            console.log(`🎯 Collaborative puzzle: ${turnColor === 'w' ? 'White' : 'Black'} to move, board oriented from ${orientation}'s perspective`);
        } catch (error) {
            console.error('Error resetting game for new puzzle:', error);
        }
    }, [activePuzzle.id, activePuzzle.fen]);

    // Join session on mount if not already a participant
    useEffect(() => {
        if (!hasJoinedRef.current) {
            hasJoinedRef.current = true;
            joinCollaborativeSession(sessionId, subscriptionId, userId, userName, userRole)
                .catch(error => {
                    // Silently handle if already a participant or other expected errors
                    console.log('Join session result:', error.message);
                });
        }
    }, [sessionId, subscriptionId, userId, userName, userRole]);

    // Subscribe to session updates
    useEffect(() => {
            unsubscribeSessionRef.current = subscribeToSession(sessionId, subscriptionId, (updatedSession) => {
            if (updatedSession) {
                setSession(updatedSession);
                // For teacher view, participants are handled by subscribeToParticipantsList
                // For student view, we still update from session
                if (!isTeacher && updatedSession.participants) {
                    const participantsWithMoves = Object.entries(updatedSession.participants || {}).reduce((acc, [id, p]) => {
                        acc[id] = {
                            ...p,
                            moves: p.moves || []
                        };
                        return acc;
                    }, {} as { [userId: string]: ParticipantBoardState });
                    setParticipants(participantsWithMoves);
                }
            }
        });

        return () => {
            if (unsubscribeSessionRef.current) {
                unsubscribeSessionRef.current();
            }
        };
    }, [sessionId, subscriptionId, isTeacher]);

    // Subscribe to participants list for teacher view (detects new joins in real-time)
    useEffect(() => {
        console.log('🔍 Teacher view subscription effect:', { isTeacher, sessionId, subscriptionId });
        if (isTeacher && sessionId && subscriptionId) {
            console.log('✅ Setting up participants list subscription...');
            // Clean up any existing subscription first
            if (unsubscribeParticipantsListRef.current) {
                console.log('🧹 Cleaning up existing subscription');
                unsubscribeParticipantsListRef.current();
            }
            
            // Subscribe to the participants list to detect new joins immediately
            unsubscribeParticipantsListRef.current = subscribeToParticipantsList(
                sessionId,
                subscriptionId,
                (participantsList) => {
                    console.log('📋 Participants list updated:', participantsList);
                    console.log('📋 Participants list keys:', participantsList ? Object.keys(participantsList) : 'null');
                    if (!participantsList) {
                        console.log('⚠️ No participants list, clearing state');
                        setParticipants({});
                        return;
                    }
                    
                    // Get all active student participants
                    const activeStudents = Object.entries(participantsList)
                        .filter(([participantId, p]) => {
                            const isActive = p && p.isActive;
                            const isStudent = p && p.userRole === 'student';
                            console.log(`Participant ${participantId}:`, { isActive, isStudent, userName: p?.userName, userRole: p?.userRole });
                            return isActive && isStudent;
                        });
                    
                    console.log('👨‍🎓 Active students found:', activeStudents.length, activeStudents.map(([, p]) => p.userName));
                    
                    // First, immediately update state with all current participants (so boards render right away)
                    const updatedParticipants: { [userId: string]: ParticipantBoardState } = {};
                    activeStudents.forEach(([participantId, participant]) => {
                        updatedParticipants[participantId] = {
                            ...participant,
                            moves: participant.moves || [],
                            boardState: participant.boardState || activePuzzle.fen // Ensure boardState exists
                        };
                    });
                    console.log('✅ Setting participants state:', Object.keys(updatedParticipants).length, 'students');
                    setParticipants(updatedParticipants);
                    
                    // Then, subscribe to individual participant updates for real-time board changes
                    activeStudents.forEach(([participantId, participant]) => {
                        if (!subscribedParticipantIdsRef.current.has(participantId)) {
                            console.log('🆕 New participant detected, subscribing to board updates:', participantId, participant.userName);
                            subscribedParticipantIdsRef.current.add(participantId);
                            
                const unsub = subscribeToParticipant(
                    sessionId,
                    subscriptionId,
                    participantId,
                                (participantData) => {
                                    if (participantData && participantData.isActive) {
                                        console.log('🔄 Real-time board update for:', participantData.userName, {
                                            boardState: participantData.boardState,
                                            moves: participantData.moves?.length || 0
                                        });
                            setParticipants(prev => ({
                                ...prev,
                                            [participantId]: {
                                                ...participantData,
                                                moves: participantData.moves || [],
                                                boardState: participantData.boardState || activePuzzle.fen
                                            }
                                        }));
                                    } else {
                                        // Participant left or became inactive
                                        console.log('👋 Participant left or became inactive:', participantId);
                                        subscribedParticipantIdsRef.current.delete(participantId);
                                        setParticipants(prev => {
                                            const updated = { ...prev };
                                            delete updated[participantId];
                                            return updated;
                                        });
                        }
                    }
                );
                unsubscribeParticipantsRef.current.push(unsub);
                        }
                    });
                    
                    // Remove subscriptions for participants who left
                    const activeStudentIds = activeStudents.map(([id]) => id);
                    const currentSubscribedIds = Array.from(subscribedParticipantIdsRef.current);
                    currentSubscribedIds.forEach(subscribedId => {
                        // Use indexOf for IE compatibility instead of includes
                        if (activeStudentIds.indexOf(subscribedId) === -1) {
                            console.log('👋 Participant left, unsubscribing:', subscribedId);
                            subscribedParticipantIdsRef.current.delete(subscribedId);
                            setParticipants(prev => {
                                const updated = { ...prev };
                                delete updated[subscribedId];
                                return updated;
                            });
                        }
                    });
                }
            );
        } else {
            console.log('❌ Not setting up subscription:', { isTeacher, sessionId, subscriptionId });
        }

        return () => {
            console.log('🧹 Cleaning up participants subscriptions');
            if (unsubscribeParticipantsListRef.current) {
                unsubscribeParticipantsListRef.current();
                unsubscribeParticipantsListRef.current = null;
            }
            unsubscribeParticipantsRef.current.forEach(unsub => unsub());
            unsubscribeParticipantsRef.current = [];
            subscribedParticipantIdsRef.current.clear();
        };
    }, [isTeacher, sessionId, subscriptionId, activePuzzle.fen]);

    // Update board state in real-time database when moves are made
    const updateBoardState = useCallback((newFen: string, newMoves: string[], isPuzzleSolved?: boolean) => {
        console.log('📤 Updating board state to Realtime DB:', { userId, userName, fen: newFen, moves: newMoves.length, isPuzzleSolved });
        updateParticipantBoardState(
            sessionId,
            subscriptionId,
            userId,
            newFen,
            newMoves,
            isPuzzleSolved
        );
    }, [sessionId, subscriptionId, userId, userName]);

    // Track puzzle progress for real-time updates
    const [puzzleProgress, setPuzzleProgress] = useState<string[]>([]);
    const [isCorrectMove, setIsCorrectMove] = useState(false);
    
    // Handle puzzle solved callback
    const handlePuzzleSolved = useCallback(() => {
        setPuzzleCompleted(true);
        updateBoardState(game.fen(), puzzleProgress, true);
        toast.success('🎉 Puzzle solved! Great job!');
        if (serviceProviderId && userId && sessionId) {
            trackChequeredCollabPuzzleSolved({
                service_provider_id: serviceProviderId,
                user_id: userId,
                subscription_id: subscriptionId,
                user_name: userName,
                user_role: userRole,
                session_id: sessionId,
                puzzle_id: activePuzzle.id,
                role: userRole
            });
        }
    }, [game, puzzleProgress, updateBoardState, serviceProviderId, userId, sessionId, subscriptionId, userName, userRole, activePuzzle.id]);

    // Parse puzzle moves (filter out move numbers and results)
    const getExpectedMoves = useCallback(() => {
        const moveParts = activePuzzle.moves.split(' ').filter(m => {
            if (!m || m.trim() === '') return false;
            // Filter out move numbers like "1.", "2.", "10."
            if (m.match(/^\d+\.?$/)) return false;
            // Filter out game results
            if (m.match(/^(1-0|0-1|1\/2-1\/2|\*)$/)) return false;
            return true;
        });
        return moveParts;
    }, [activePuzzle.moves]);

    // Handle piece drop with full puzzle validation
    const onDrop = useCallback((sourceSquare: string, targetSquare: string) => {
        if (puzzleCompleted && !isTeacher) {
            toast.info('Puzzle already solved!');
            return false;
        }

        try {
            const gameCopy = new Chess(game.fen());
            const move = gameCopy.move({
                from: sourceSquare,
                to: targetSquare,
                promotion: 'q'
            });

            if (move === null) {
                return false;
            }

            const expectedMoves = getExpectedMoves();
            const currentMoveIndex = puzzleProgress.length;
            
            // Check if this is the correct move
            const isCorrect = (move.san === expectedMoves[currentMoveIndex] || 
                move.san.replace(/[+#]/, '') === expectedMoves[currentMoveIndex]?.replace(/[+#]/, '') ||
                `${sourceSquare}${targetSquare}` === expectedMoves[currentMoveIndex]);

            if (isCorrect) {
                // Play move sound
                if (move.captured) {
                    chessSounds.playCapture();
                } else {
                    chessSounds.playMove();
                }
                
            setGame(gameCopy);
                const newProgress = [...puzzleProgress, move.san];
                setPuzzleProgress(newProgress);
                setIsCorrectMove(true);
                
                // Update Realtime DB immediately with correct move
                updateBoardState(gameCopy.fen(), newProgress);

            // Check if puzzle is completed
                if (newProgress.length === expectedMoves.length) {
                    setPuzzleCompleted(true);
                    chessSounds.playPuzzleComplete();
                    updateBoardState(gameCopy.fen(), newProgress, true);
                    toast.success('🎉 Puzzle solved! Great job!');
                    if (serviceProviderId && userId && sessionId) {
                        trackChequeredCollabPuzzleSolved({
                            service_provider_id: serviceProviderId,
                            user_id: userId,
                            subscription_id: subscriptionId,
                            user_name: userName,
                            user_role: userRole,
                            session_id: sessionId,
                            puzzle_id: activePuzzle.id,
                            role: userRole
                        });
                    }
                } else {
                    chessSounds.playCorrect(); // Play correct move sound
                    toast.success('✅ Correct move!');
                    
                    // Make computer's response move after a delay
                    setTimeout(() => {
                        if (currentMoveIndex + 1 < expectedMoves.length) {
                            const nextMove = expectedMoves[currentMoveIndex + 1];
                            try {
                                const computerMove = gameCopy.move(nextMove);
                                if (computerMove) {
                                    const updatedGame = new Chess(gameCopy.fen());
                                    const updatedProgress = [...newProgress, computerMove.san];
                                    setGame(updatedGame);
                                    setPuzzleProgress(updatedProgress);
                                    
                                    // Play computer move sound
                                    if (computerMove.captured) {
                                        chessSounds.playCapture();
                                    } else {
                                        chessSounds.playMove();
                                    }
                                    
                                    // Play check sound if king is in check
                                    if (updatedGame.inCheck()) {
                                        setTimeout(() => chessSounds.playCheck(), 100);
                                    }
                                    
                                    // Update Realtime DB with computer move
                                    updateBoardState(updatedGame.fen(), updatedProgress);
                                    
                                    // Check if puzzle completed after computer move
                                    if (updatedProgress.length === expectedMoves.length) {
                                        setPuzzleCompleted(true);
                                        chessSounds.playPuzzleComplete();
                                        updateBoardState(updatedGame.fen(), updatedProgress, true);
                                        toast.success('🎉 Puzzle solved! Great job!');
                                        if (serviceProviderId && userId && sessionId) {
                                            trackChequeredCollabPuzzleSolved({
                                                service_provider_id: serviceProviderId,
                                                user_id: userId,
                                                subscription_id: subscriptionId,
                                                user_name: userName,
                                                user_role: userRole,
                                                session_id: sessionId,
                                                puzzle_id: activePuzzle.id,
                                                role: userRole
                                            });
                                        }
                                    }
                                }
                            } catch (e) {
                                console.error('Error making computer move:', e);
                            }
                        }
                    }, 500);
                }
            } else {
                setIsCorrectMove(false);
                chessSounds.playWrong(); // Play wrong move sound
                toast.error('❌ Incorrect move. Try again!');
                
                // Trigger shake animation
                setBoardShake(true);
                setTimeout(() => setBoardShake(false), 500);
                
                return false;
            }

            return true;
        } catch (error) {
            console.error('Error making move:', error);
            return false;
        }
    }, [game, puzzleProgress, puzzleCompleted, isTeacher, getExpectedMoves, updateBoardState, handlePuzzleSolved]);

    // Reset puzzle
    const resetPuzzle = useCallback(() => {
        const newGame = new Chess(activePuzzle.fen);
        setGame(newGame);
        setPuzzleProgress([]);
        setPuzzleCompleted(false);
        setIsCorrectMove(false);
        updateBoardState(newGame.fen(), [], false); // Clear solved status
        toast.info('Puzzle reset');
    }, [activePuzzle.fen, updateBoardState]);

    // Leave session
    const handleLeave = () => {
        // If teacher, show warning about deleting session
        if (isTeacher) {
            setShowDeleteWarning(true);
        } else {
            trackChequeredCollabSessionEnded({
                service_provider_id: serviceProviderId,
                user_id: userId,
                subscription_id: subscriptionId,
                user_name: userName,
                user_role: userRole,
                session_id: sessionId,
                ended_by: 'student'
            });
            leaveCollaborativeSession(sessionId, subscriptionId, userId);
            onClose();
        }
    };

    // Handle deleting session (teacher only)
    const handleDeleteSession = async () => {
        setIsDeleting(true);
        try {
            await deleteCollaborativeSession(sessionId, subscriptionId);
            toast.success('Session deleted successfully');
            onClose();
        } catch (error) {
            console.error('Error deleting session:', error);
            toast.error('Failed to delete session');
            setIsDeleting(false);
        }
    };

    // End session (teacher only)
    const handleEndSession = () => {
        if (isTeacher) {
            trackChequeredCollabSessionEnded({
                service_provider_id: serviceProviderId,
                user_id: userId,
                subscription_id: subscriptionId,
                user_name: userName,
                user_role: userRole,
                session_id: sessionId,
                ended_by: 'teacher'
            });
            endCollaborativeSession(sessionId, subscriptionId);
            toast.success('Session ended');
            onClose();
        }
    };

    // Get participant count
    const participantCount = Object.keys(participants).filter(
        id => participants[id]?.isActive
    ).length;

    return (
        <div className="h-full w-full flex flex-col bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 px-6 py-3 flex-shrink-0">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <h2 className="text-lg font-bold text-gray-900">
                            🤝 Collaborative Puzzle Solving
                        </h2>
                        <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                            {participantCount} {participantCount === 1 ? 'participant' : 'participants'}
                        </span>
                        {/* Role indicator */}
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                            isTeacher 
                                ? 'bg-green-100 text-green-700 border-2 border-green-300' 
                                : 'bg-blue-100 text-blue-700 border-2 border-blue-300'
                        }`}>
                            {isTeacher ? '👨‍🏫 Teacher' : '👨‍🎓 Student'}
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        {/* Share Link - Only visible to teachers */}
                        {isTeacher && (
                        <button
                            onClick={async () => {
                                try {
                                        // Construct the shareable link with student role parameter
                                        const shareLink = `${window.location.origin}/chequered/collaborative/${sessionId}?role=student`;
                                    
                                    // Browser-compatible clipboard copy with fallback for IE and older Firefox
                                    if (navigator.clipboard && navigator.clipboard.writeText) {
                                        await navigator.clipboard.writeText(shareLink);
                                        trackChequeredCollabShareLinkCopied({
                                            service_provider_id: serviceProviderId,
                                            user_id: userId,
                                            subscription_id: subscriptionId,
                                            user_name: userName,
                                            user_role: userRole,
                                            session_id: sessionId
                                        });
                                        toast.success('🔗 Student link copied! Share it with students to join the puzzle session.');
                                    } else {
                                        // Fallback for IE and older browsers
                                        const textArea = document.createElement('textarea');
                                        textArea.value = shareLink;
                                        textArea.style.position = 'fixed';
                                        textArea.style.left = '-999999px';
                                        textArea.style.top = '-999999px';
                                        document.body.appendChild(textArea);
                                        textArea.focus();
                                        textArea.select();
                                        
                                        try {
                                            const successful = document.execCommand('copy');
                                            if (successful) {
                                                toast.success('🔗 Student link copied! Share it with students to join the puzzle session.');
                                            } else {
                                                throw new Error('Copy command failed');
                                            }
                                        } catch (err) {
                                            // If copy fails, show the link in a prompt
                                            const userInput = prompt('Copy this link to share with students:', shareLink);
                                            if (userInput !== null) {
                                                toast.info('Link displayed in prompt');
                                            } else {
                                                toast.error('Failed to copy link');
                                            }
                                        } finally {
                                            document.body.removeChild(textArea);
                                        }
                                    }
                                } catch (error) {
                                    console.error('Failed to copy link:', error);
                                    // Fallback: show link in prompt
                                    const shareLink = `${window.location.origin}/chequered/collaborative/${sessionId}?role=student`;
                                    const userInput = prompt('Copy this link to share with students:', shareLink);
                                    if (userInput !== null) {
                                        toast.info('Link displayed in prompt');
                                    } else {
                                        toast.error('Failed to copy link');
                                    }
                                }
                            }}
                            className="px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
                            title="Copy shareable link to invite students"
                        >
                            <span>🔗</span>
                            Share Student Link
                        </button>
                        )}
                        {isTeacher && (
                            <button
                                onClick={handleEndSession}
                                className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-medium transition-colors"
                            >
                                End Session
                            </button>
                        )}
                        <button
                            onClick={handleLeave}
                            className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg text-sm font-medium transition-colors"
                        >
                            Leave
                        </button>
                    </div>
                </div>
                <div className="mt-2">
                    <p className="text-sm text-gray-700">
                        <strong>{activePuzzle.description}</strong> • Rating: {activePuzzle.rating} • {activePuzzle.complexity}
                    </p>
                </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 overflow-hidden flex flex-row gap-4 p-4">
                {isTeacher ? (
                    <>
                        {/* Teacher: Class puzzles list (left sidebar) */}
                        {availablePuzzles && availablePuzzles.length > 0 && (
                            <div className="w-56 bg-white rounded-lg shadow-md border-2 border-gray-200 flex-shrink-0 flex flex-col overflow-hidden" style={{ maxHeight: 'calc(100vh - 180px)' }}>
                                <h3 className="font-bold text-gray-900 p-3 pb-2 border-b border-gray-200 text-sm flex-shrink-0">
                                    📋 Class puzzles
                                </h3>
                                <div className="flex gap-1 p-2 border-b border-gray-100 flex-shrink-0">
                                    <button
                                        type="button"
                                        onClick={() => setPuzzleFilter('all')}
                                        className={`flex-1 px-2 py-1 text-xs font-medium rounded ${puzzleFilter === 'all' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                                    >
                                        All
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setPuzzleFilter('unsolved')}
                                        className={`flex-1 px-2 py-1 text-xs font-medium rounded ${puzzleFilter === 'unsolved' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                                    >
                                        Unsolved
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setPuzzleFilter('solved')}
                                        className={`flex-1 px-2 py-1 text-xs font-medium rounded ${puzzleFilter === 'solved' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                                    >
                                        Solved
                                    </button>
                                </div>
                                <div className="flex-1 overflow-y-auto p-2 space-y-1">
                                    {puzzleListLoading ? (
                                        <p className="text-xs text-gray-500 p-2">Loading...</p>
                                    ) : filteredPuzzles.length === 0 ? (
                                        <p className="text-xs text-gray-500 p-2">
                                            {puzzleFilter === 'all' ? 'No puzzles' : `No ${puzzleFilter} puzzles`}
                                        </p>
                                    ) : (
                                        filteredPuzzles.map((p) => {
                                            const isActive = p.id === activePuzzle.id;
                                            return (
                                                <div
                                                    key={p.id}
                                                    onClick={() => handleSwitchPuzzle(p.id)}
                                                    className={`p-2 rounded-lg border-2 cursor-pointer transition-all text-left ${
                                                        isActive
                                                            ? 'border-purple-500 bg-purple-50'
                                                            : 'border-gray-200 hover:border-purple-300 bg-gray-50 hover:bg-purple-50/50'
                                                    }`}
                                                >
                                                    <div className="flex items-start justify-between gap-1">
                                                        <span className="text-xs font-medium text-gray-900 truncate flex-1" title={p.description || p.id}>
                                                            {p.description || `Puzzle ${p.id.slice(0, 6)}`}
                                                        </span>
                                                        {p.isSolved && <span className="text-green-600 flex-shrink-0" title="Solved">✓</span>}
                                                    </div>
                                                    <div className="text-[10px] text-gray-500 mt-0.5">
                                                        {p.rating} • {p.complexity}
                                                    </div>
                                                    {isActive && (
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleMarkAsSolved(p.id);
                                                            }}
                                                            disabled={p.isSolved}
                                                            className="mt-2 w-full py-1 text-[10px] font-medium rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                                        >
                                                            {p.isSolved ? 'Solved' : 'Mark solved'}
                                                        </button>
                                                    )}
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            </div>
                        )}
                        {/* Teacher View: Show all participants' boards separately */}
                    <div className="flex-1 overflow-auto">
                            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                <div className="flex items-center justify-between">
                                    <p className="text-sm text-blue-800 font-medium">
                                        👁️ Viewing {Object.keys(participants).filter(id => participants[id]?.isActive && participants[id]?.userRole === 'student').length} student{Object.keys(participants).filter(id => participants[id]?.isActive && participants[id]?.userRole === 'student').length !== 1 ? 's' : ''}
                                    </p>
                                    <p className="text-xs text-blue-600 font-medium">
                                        Max: 10 students
                                    </p>
                                </div>
                                {/* Debug info */}
                                {process.env.NODE_ENV === 'development' && (
                                    <div className="mt-2 text-xs text-gray-600">
                                        Debug: Total participants in state: {Object.keys(participants).length} | 
                                        Active students: {Object.keys(participants).filter(id => participants[id]?.isActive && participants[id]?.userRole === 'student').length}
                                    </div>
                                )}
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                                {(() => {
                                    const allParticipants = Object.entries(participants);
                                    const studentParticipants = allParticipants.filter(([id, p]) => {
                                        const isValid = p && p.isActive && p.userRole === 'student';
                                        if (p && !isValid) {
                                            console.log('🚫 Filtered out participant:', id, { 
                                                isActive: p.isActive, 
                                                userRole: p.userRole,
                                                userName: p.userName 
                                            });
                                        }
                                        return isValid;
                                    });
                                    console.log('🎨 Rendering:', {
                                        totalParticipants: allParticipants.length,
                                        studentCount: studentParticipants.length,
                                        students: studentParticipants.map(([id, p]) => ({ id, name: p.userName, role: p.userRole, active: p.isActive }))
                                    });
                                    return studentParticipants.map(([participantId, participant]) => (
                                        <div
                                            key={participantId}
                                            className={`bg-white rounded-lg shadow-lg p-4 border-2 transition-all ${
                                                participant.isPuzzleSolved 
                                                    ? 'border-green-500 hover:border-green-600 bg-green-50' 
                                                    : 'border-gray-300 hover:border-purple-400'
                                            }`}
                                            style={{ minHeight: '320px' }}
                                        >
                                            <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-200">
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <h3 className="font-bold text-gray-900 text-sm truncate" title={participant.userName}>
                                                            {participant.userName}
                                                        </h3>
                                                        {participant.isPuzzleSolved && (
                                                            <span className="px-2 py-0.5 bg-green-500 text-white rounded-full text-xs font-bold flex-shrink-0">
                                                                ✓ Solved
                                                            </span>
                                                        )}
                                                    </div>
                                                    <p className="text-xs text-gray-500 mt-1">
                                                        {participant.userRole === 'teacher' ? '👨‍🏫 Teacher' : '👨‍🎓 Student'}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex justify-center mb-3">
                                                <Chessboard
                                                    key={`board-${participantId}-${participant.boardState || activePuzzle.fen}`}
                                                    position={participant.boardState || activePuzzle.fen}
                                                    arePiecesDraggable={false}
                                                    boardOrientation={boardOrientation}
                                                    boardWidth={220}
                                                    customDarkSquareStyle={{ backgroundColor: '#b58863' }}
                                                    customLightSquareStyle={{ backgroundColor: '#f0d9b5' }}
                                                />
                                            </div>
                                            <div className="mt-3 pt-3 border-t border-gray-200">
                                                <div className="flex items-center justify-between mb-2">
                                                    <p className="text-xs font-semibold text-gray-700">Progress:</p>
                                                    <span className={`px-2 py-1 rounded text-xs font-bold ${
                                                        participant.isPuzzleSolved 
                                                            ? 'bg-green-100 text-green-700' 
                                                            : 'bg-purple-100 text-purple-700'
                                                    }`}>
                                                        {(() => {
                                                            // Count only student moves (every other move, starting from index 0)
                                                            const studentMoveCount = Math.ceil((participant.moves || []).length / 2);
                                                            const totalStudentMoves = Math.ceil(getExpectedMoves().length / 2);
                                                            return participant.isPuzzleSolved 
                                                                ? `✓ ${studentMoveCount}/${totalStudentMoves}`
                                                                : `${studentMoveCount}/${totalStudentMoves}`;
                                                        })()}
                                                    </span>
                                                </div>
                                                {participant.isPuzzleSolved && (
                                                    <div className="mt-2 mb-2 p-2 bg-green-100 border border-green-300 rounded text-center">
                                                        <p className="text-xs font-bold text-green-800">🎉 Puzzle Solved!</p>
                                                    </div>
                                                )}
                                                {(participant.moves || []).length > 0 && (
                                                    <div className="mt-2 p-2 bg-gray-50 rounded text-xs">
                                                        <p className="text-[10px] text-gray-500 mb-1">Moves made:</p>
                                                        <p className="font-mono text-[10px] text-gray-600 break-all">
                                                            {(participant.moves || []).join(' → ')}
                                                        </p>
                                                    </div>
                                                )}
                                                {(participant.moves || []).length === 0 && (
                                                    <p className="text-xs text-gray-400 italic">No moves yet</p>
                                                )}
                                            </div>
                                        </div>
                                    ));
                                })()}
                                {Object.keys(participants).filter(id => participants[id]?.isActive && participants[id]?.userRole === 'student').length === 0 && (
                                    <div className="col-span-full text-center py-12">
                                        <p className="text-gray-500">No students joined yet. Share the link to invite students!</p>
                                    </div>
                                )}
                            </div>
                        </div>
                        {/* Participants List Sidebar - Right Side */}
                        <div className="w-64 bg-white rounded-lg shadow-md p-4 border-2 border-gray-200 flex-shrink-0 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 200px)' }}>
                        <h3 className="font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200 text-sm">
                            👥 Participants ({Object.keys(participants).filter(id => participants[id]?.isActive).length})
                        </h3>
                        <div className="space-y-2">
                            {Object.entries(participants)
                                .filter(([, p]) => p && p.isActive)
                                .map(([participantId, participant]) => (
                                    <div
                                        key={participantId}
                                        className={`p-3 rounded-lg border-2 transition-all ${
                                            participant.userId === userId
                                                ? 'bg-blue-50 border-blue-300'
                                                : participant.userRole === 'teacher'
                                                ? 'bg-purple-50 border-purple-300'
                                                : 'bg-gray-50 border-gray-200 hover:border-purple-300'
                                        }`}
                                    >
                                        <div className="flex items-center justify-between mb-1">
                                            <p className="font-semibold text-sm text-gray-900 truncate flex-1" title={participant.userName}>
                                                    {participant.userName}
                                                </p>
                                            {participant.userId === userId && (
                                                <span className="px-2 py-0.5 bg-blue-200 text-blue-700 rounded text-xs font-medium ml-1 flex-shrink-0">
                                                    You
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex items-center justify-between mb-2">
                                            <p className="text-xs text-gray-600">
                                                {participant.userRole === 'teacher' ? '👨‍🏫 Teacher' : '👨‍🎓 Student'}
                                            </p>
                                            <p className="text-xs font-medium text-purple-600">
                                                {Math.ceil((participant.moves || []).length / 2)} moves
                                            </p>
                                        </div>
                                        {participant.userRole === 'student' && (
                                            <div className="mt-2 pt-2 border-t border-gray-200">
                                                <div className="flex items-center justify-between mb-1">
                                                    <p className="text-xs text-gray-500">Progress:</p>
                                                    <p className="text-xs font-medium text-gray-700">
                                                        {Math.ceil((participant.moves || []).length / 2)} / {Math.ceil(getExpectedMoves().length / 2)}
                                                    </p>
                                                </div>
                                                <div className="w-full bg-gray-200 rounded-full h-1.5">
                                                    <div
                                                        className="bg-purple-600 h-1.5 rounded-full transition-all"
                                                        style={{
                                                            width: `${Math.min(100, (Math.ceil((participant.moves || []).length / 2) / Math.max(1, Math.ceil(getExpectedMoves().length / 2))) * 100)}%`
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            {Object.keys(participants).filter(id => participants[id]?.isActive).length === 0 && (
                                <p className="text-sm text-gray-500 text-center py-4">No participants yet</p>
                            )}
                        </div>
                    </div>
                    </>
                ) : (
                    // Student View: Full puzzle solving with validation (uses activePuzzle so it refreshes when teacher switches)
                    <div className="flex-1 flex flex-col items-center justify-center overflow-auto p-4">
                        <div className="bg-white rounded-xl shadow-lg p-6 max-w-4xl w-full">
                            {/* Puzzle Info */}
                            <div className="mb-4 p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-200">
                                <h3 className="text-lg font-bold text-gray-900 mb-2">{activePuzzle.description}</h3>
                                <div className="flex flex-wrap gap-3 text-sm">
                                    <span className="px-2 py-1 bg-white rounded text-gray-700">⭐ Rating: {activePuzzle.rating}</span>
                                    <span className="px-2 py-1 bg-white rounded text-gray-700">📊 {activePuzzle.complexity}</span>
                                </div>
                            </div>

                            {/* Chess Board */}
                            <div className={`flex justify-center mb-4 ${boardShake ? 'board-shake' : ''}`}>
                                <Chessboard
                                    position={game.fen()}
                                    onPieceDrop={onDrop}
                                    arePiecesDraggable={!puzzleCompleted}
                                    boardOrientation={boardOrientation}
                                    boardWidth={Math.min(600, window.innerWidth * 0.7)}
                                    customDarkSquareStyle={{ backgroundColor: '#b58863' }}
                                    customLightSquareStyle={{ backgroundColor: '#f0d9b5' }}
                                />
                                
                                {/* Shake Animation CSS */}
                                <style>{`
                                    @keyframes shake {
                                        0%, 100% { transform: translateX(0); }
                                        10%, 30%, 50%, 70%, 90% { transform: translateX(-8px); }
                                        20%, 40%, 60%, 80% { transform: translateX(8px); }
                                    }
                                    
                                    .board-shake {
                                        animation: shake 0.5s ease-in-out;
                                    }
                                `}</style>
                            </div>
                            
                            {/* Puzzle Status */}
                            {puzzleCompleted && (
                                <div className="mb-4 p-4 bg-green-100 border-2 border-green-400 rounded-lg text-center">
                                    <p className="text-green-800 font-bold text-lg">🎉 Puzzle Solved! Great job!</p>
                                </div>
                            )}

                            {isCorrectMove && !puzzleCompleted && (
                                <div className="mb-4 p-3 bg-green-50 border border-green-300 rounded-lg text-center">
                                    <p className="text-green-700 font-medium">✅ Correct move!</p>
                                </div>
                            )}

                            {/* Controls */}
                            <div className="flex flex-wrap gap-2 justify-center mb-4">
                                <button
                                    onClick={resetPuzzle}
                                    disabled={puzzleCompleted}
                                    className="px-4 py-2 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors"
                                >
                                    🔁 Reset Puzzle
                                </button>
                                <button
                                    onClick={() => setBoardOrientation(boardOrientation === 'white' ? 'black' : 'white')}
                                    className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg text-sm font-medium transition-colors"
                                >
                                    🔄 Flip Board
                                </button>
                                <button
                                    onClick={() => {
                                        const newValue = !soundEnabled;
                                        setSoundEnabled(newValue);
                                        chessSounds.setEnabled(newValue);
                                        localStorage.setItem('chessSoundEnabled', String(newValue));
                                        toast.info(newValue ? '🔊 Sound enabled' : '🔇 Sound muted');
                                    }}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${soundEnabled ? 'bg-slate-500 hover:bg-slate-600 text-white' : 'bg-red-500 hover:bg-red-600 text-white'}`}
                                    title={soundEnabled ? 'Mute sounds' : 'Enable sounds'}
                                >
                                    {soundEnabled ? '🔊' : '🔇'} Sound
                                </button>
                            </div>

                            {/* Move Progress */}
                            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                                <div className="flex items-center justify-between mb-2">
                                    <p className="text-sm font-semibold text-gray-700">Your Progress:</p>
                                    <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                                        puzzleCompleted 
                                            ? 'bg-green-100 text-green-700' 
                                            : 'bg-purple-100 text-purple-700'
                                    }`}>
                                        {puzzleCompleted 
                                            ? `✓ Solved!`
                                            : `${Math.ceil(puzzleProgress.length / 2)} / ${Math.ceil(getExpectedMoves().length / 2)} moves`
                                        }
                                    </span>
                                </div>
                                {puzzleProgress.length > 0 && (
                                    <div className="mt-2">
                                        <p className="text-xs font-semibold text-gray-600 mb-1">Moves made:</p>
                                        <p className="font-mono text-sm text-gray-800 bg-white p-2 rounded border">
                                            {puzzleProgress.join(' → ')}
                                    </p>
                                </div>
                            )}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Delete Session Warning Dialog (Teacher Only) */}
            <Dialog
            open={showDeleteWarning}
            onClose={() => !isDeleting && setShowDeleteWarning(false)}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '12px',
                    padding: '8px'
                }
            }}
        >
            <DialogTitle sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                padding: '20px 24px 16px',
                borderBottom: '1px solid #e5e7eb'
            }}>
                <span style={{ fontSize: '1.25rem', fontWeight: 600, color: '#111827' }}>
                    ⚠️ Delete Session?
                </span>
                <IconButton
                    onClick={() => !isDeleting && setShowDeleteWarning(false)}
                    disabled={isDeleting}
                    size="small"
                    sx={{ color: '#6b7280' }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ padding: '24px' }}>
                <div className="space-y-3">
                    <p className="text-gray-700" style={{ fontSize: '0.95rem', lineHeight: '1.6' }}>
                        You are about to <strong>permanently delete</strong> this collaborative session. This action will:
                    </p>
                    <ul className="list-disc list-inside space-y-2 text-gray-600" style={{ fontSize: '0.9rem', paddingLeft: '8px' }}>
                        <li>Remove the session from the database</li>
                        <li>Disconnect all participants immediately</li>
                        <li>Release all storage associated with this session</li>
                        <li>This action <strong>cannot be undone</strong></li>
                    </ul>
                    <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-red-800 text-sm font-medium">
                            ⚠️ All participants will be disconnected and the session will be permanently deleted.
                        </p>
                    </div>
                </div>
            </DialogContent>
            <DialogActions sx={{ 
                padding: '16px 24px 20px',
                borderTop: '1px solid #e5e7eb',
                gap: '12px'
            }}>
                <Button
                    onClick={() => setShowDeleteWarning(false)}
                    disabled={isDeleting}
                    variant="outlined"
                    sx={{
                        color: '#6b7280',
                        borderColor: '#d1d5db',
                        '&:hover': {
                            borderColor: '#9ca3af',
                            backgroundColor: '#f9fafb'
                        }
                    }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleDeleteSession}
                    disabled={isDeleting}
                    variant="contained"
                    sx={{
                        backgroundColor: '#ef4444',
                        color: 'white',
                        '&:hover': {
                            backgroundColor: '#dc2626'
                        },
                        '&:disabled': {
                            backgroundColor: '#fca5a5',
                            color: 'white'
                        }
                    }}
                >
                    {isDeleting ? 'Deleting...' : 'Delete Session'}
                </Button>
            </DialogActions>
        </Dialog>
        </div>
    );
};

export default CollaborativePuzzleView;
